/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import {
  CRDTContainer,
  CRDTElement,
} from '@yorkie-js/sdk/src/document/crdt/element';
import {
  RGATreeList,
  RGATreeListNode,
} from '@yorkie-js/sdk/src/document/crdt/rga_tree_list';
import * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { DataSize } from '@yorkie-js/sdk/src/util/resource';

/**
 * `CRDTArray` represents an array data type containing `CRDTElement`s.
 */
export class CRDTArray extends CRDTContainer {
  private elements: RGATreeList;

  constructor(createdAt: TimeTicket, elements: RGATreeList) {
    super(createdAt);
    this.elements = elements;
  }

  /**
   * `create` creates a new instance of Array.
   */
  public static create(
    createdAt: TimeTicket,
    value?: Array<CRDTElement>,
  ): CRDTArray {
    if (!value) {
      return new CRDTArray(createdAt, RGATreeList.create());
    }

    const elements = RGATreeList.create();
    for (const v of value) {
      elements.insertAfter(elements.getLastCreatedAt(), v.deepcopy());
    }
    return new CRDTArray(createdAt, elements);
  }

  /**
   * `subPathOf` returns the sub path of the given element.
   */
  public subPathOf(createdAt: TimeTicket): string | undefined {
    return this.elements.subPathOf(createdAt);
  }

  /**
   * `purge` physically purge the given element.
   */
  public purge(element: CRDTElement): void {
    this.elements.purge(element);
  }

  /**
   * `insertAfter` adds a new node after the the given node.
   */
  public insertAfter(
    prevCreatedAt: TimeTicket,
    value: CRDTElement,
    executedAt?: TimeTicket,
  ): void {
    this.elements.insertAfter(prevCreatedAt, value, executedAt);
  }

  /**
   * `moveAfter` moves the given `createdAt` element after the
   * `prevCreatedAt`. Returns the dead position node for GC.
   */
  public moveAfter(
    prevCreatedAt: TimeTicket,
    createdAt: TimeTicket,
    executedAt: TimeTicket,
  ): RGATreeListNode | undefined {
    return this.elements.moveAfter(prevCreatedAt, createdAt, executedAt);
  }

  /**
   * `get` returns the element of the given index.
   */
  public get(index: number): CRDTElement | undefined {
    const node = this.elements.getByIndex(index);
    return node?.getValue();
  }

  /**
   * `getByID` returns the element of the given createAt.
   */
  public getByID(createdAt: TimeTicket): CRDTElement | undefined {
    const node = this.elements.getByID(createdAt);
    return node?.getValue();
  }

  /**
   * `getHead` returns dummy head element.
   */
  public getHead(): CRDTElement {
    return this.elements.getHead();
  }

  /**
   * `getLast` returns last element.
   */
  public getLast(): CRDTElement {
    return this.elements.getLast();
  }

  /**
   * `getPrevCreatedAt` returns the creation time of the previous
   * node.
   */
  public getPrevCreatedAt(createdAt: TimeTicket): TimeTicket {
    return this.elements.getPrevCreatedAt(createdAt);
  }

  /**
   * `posCreatedAt` returns the createdAt of the position node
   * currently holding the element. Used to convert element identity
   * to position identity for moves.
   */
  public posCreatedAt(elemCreatedAt: TimeTicket): TimeTicket {
    return this.elements.posCreatedAt(elemCreatedAt);
  }

  /**
   * `delete` deletes the element of the given creation time.
   */
  public delete(createdAt: TimeTicket, editedAt: TimeTicket): CRDTElement {
    return this.elements.delete(createdAt, editedAt);
  }

  /**
   * `deleteByIndex` deletes the element of given index and editedAt.
   */
  public deleteByIndex(
    index: number,
    editedAt: TimeTicket,
  ): CRDTElement | undefined {
    return this.elements.deleteByIndex(index, editedAt);
  }

  /**
   * `set` sets the given element at the given position of the
   * creation time.
   */
  public set(
    createdAt: TimeTicket,
    value: CRDTElement,
    executedAt: TimeTicket,
  ): CRDTElement {
    return this.elements.set(createdAt, value, executedAt);
  }

  /**
   * `getLastCreatedAt` get last created element.
   */
  public getLastCreatedAt(): TimeTicket {
    return this.elements.getLastCreatedAt();
  }

  /**
   * `length` returns length of this elements.
   */
  public get length(): number {
    return this.elements.length;
  }

  /**
   * `[Symbol.iterator]` returns an iterator for the elements in
   * this array.
   */
  public *[Symbol.iterator](): IterableIterator<CRDTElement> {
    for (const node of this.elements) {
      if (node.getElementEntry() && !node.isRemoved()) {
        yield node.getValue();
      }
    }
  }

  /**
   * `toTestString` returns a String containing the meta data of
   * this value for debugging purpose.
   */
  public toTestString(): string {
    return this.elements.toTestString();
  }

  /**
   * `getDescendants` traverse the descendants of this array.
   */
  public getDescendants(
    callback: (elem: CRDTElement, parent: CRDTContainer) => boolean,
  ): void {
    for (const node of this.elements) {
      if (!node.getElementEntry()) {
        continue;
      }
      const element = node.getValue();
      if (callback(element, this)) {
        return;
      }

      if (element instanceof CRDTContainer) {
        element.getDescendants(callback);
      }
    }
  }

  /**
   * `getDataSize` returns the data usage of this element.
   */
  public getDataSize(): DataSize {
    return {
      data: 0,
      meta: this.getMetaUsage(),
    };
  }

  /**
   * `toJSON` returns the JSON encoding of this array.
   */
  public toJSON(): string {
    const json = [];
    for (const value of this) {
      json.push(value.toJSON());
    }
    return `[${json.join(',')}]`;
  }

  /**
   * `toJS` return the javascript object of this array.
   */
  public toJS(): any {
    return JSON.parse(this.toJSON());
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    const values: Devtools.ContainerValue = {};
    for (let i = 0; i < this.length; i++) {
      const { createdAt, value, type } = this.get(i)!.toJSForTest();
      values[i] = {
        key: String(i),
        createdAt,
        value,
        type,
      };
    }
    return {
      createdAt: this.getCreatedAt().toTestString(),
      value: values,
      type: 'YORKIE_ARRAY',
    };
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of this array.
   */
  public toSortedJSON(): string {
    return this.toJSON();
  }

  /**
   * `getElements` returns the underlying RGATreeList.
   */
  public getElements(): RGATreeList {
    return this.elements;
  }

  /**
   * `getRGATreeList` returns the underlying RGATreeList (GCParent
   * for dead positions).
   */
  public getRGATreeList(): RGATreeList {
    return this.elements;
  }

  /**
   * `getAllRGANodes` returns all RGA nodes including dead position
   * nodes.
   */
  public getAllRGANodes(): Array<RGATreeListNode> {
    return this.elements.allNodes();
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): CRDTArray {
    const clone = CRDTArray.create(this.getCreatedAt());
    for (const node of this.elements) {
      if (!node.getElementEntry()) {
        // Dead position node (abandoned by a move).
        const removedAt = node.getRemovedAt();
        if (removedAt) {
          clone.elements.addDeadPosition(
            node.getPositionCreatedAt(),
            removedAt,
          );
        }
        continue;
      }

      const value = node.getValue().deepcopy();
      const posMovedAt = node.getPositionMovedAt();
      if (posMovedAt) {
        clone.elements.addMovedElement(
          value,
          node.getPositionCreatedAt(),
          posMovedAt,
        );
      } else {
        clone.elements.insertAfter(clone.getLastCreatedAt(), value);
      }
    }
    clone.setRemovedAt(this.getRemovedAt());
    clone.setMovedAt(this.getMovedAt());
    return clone;
  }
}
